import { useCallback, useEffect, useRef, useState } from 'react'

// Redux
import { connect } from 'react-redux';
import { signinUser } from '../redux/actions/userActions';

import { Close } from "@mui/icons-material";

// Firebase.
import { getAnalytics, logEvent } from "firebase/analytics";
import { auth, isAnonymousFirebaseUser } from '../firebaseHelper'
import PhoneSigninPanel from './PhoneSigninPanel';
import { readOnboardingFlow, trackOnboardingError, trackOnboardingEvent } from '../utils/onboardingFunnel';

const SigninButtonPopup = ({
    authenticated,
    close,
    signinUser,
    title = 'Sign in',
    subtitle = "We'll send a text to verify your phone.",
    presentation = 'popup',
    headingId,
    onSignedIn,
    onBusyChange,
    autoFocusPhone = true,
}) => {

    const [phoneStage, setPhoneStage] = useState('phone');
    const [phoneBusy, setPhoneBusy] = useState(false);
    const [signinPending, setSigninPending] = useState(false);
    const [signinError, setSigninError] = useState(null);
    const mountedRef = useRef(false);
    const signinInFlightRef = useRef(false);
    const signinCompleteRef = useRef(false);
    const signupOpenLoggedRef = useRef(false);
    const phonePanelRef = useRef(null);
    const completionCallbacksRef = useRef({ close, onSignedIn });
    completionCallbacksRef.current = { close, onSignedIn };
    const isSheet = presentation === 'sheet';
    const busy = phoneBusy || signinPending;
    const showPhoneForm = !authenticated && !signinPending && !signinError;
    const showPhoneIntroduction = showPhoneForm && phoneStage !== 'code';

    useEffect(() => {
        onBusyChange?.(busy);
    }, [busy, onBusyChange]);

    const completeSignin = useCallback(async user => {
        if (!mountedRef.current || !user || isAnonymousFirebaseUser(user)
            || signinInFlightRef.current || signinCompleteRef.current) return;
        signinInFlightRef.current = true;
        setSigninPending(true);
        setSigninError(null);
        const flowId = readOnboardingFlow()?.flowId || null;
        try {
            const idToken = await user.getIdToken();
            if (!mountedRef.current) return;
            const result = await signinUser(idToken, user.refreshToken, {});
            if (!result?.success) throw result?.error || new Error('Account setup failed');
            signinCompleteRef.current = true;
            trackOnboardingEvent('onboarding_ready', {}, { flowId });
            if (!mountedRef.current) return;
            logEvent(getAnalytics(), 'signin_button_popup:success');
            const callbacks = completionCallbacksRef.current;
            if (callbacks.onSignedIn) callbacks.onSignedIn();
            else callbacks.close();
        } catch (error) {
            trackOnboardingError('bootstrap', error, { flowId });
            if (mountedRef.current) setSigninError(error);
        } finally {
            signinInFlightRef.current = false;
            if (mountedRef.current) setSigninPending(false);
        }
    }, [signinUser]);

    useEffect(() => {
        mountedRef.current = true;
        const analytics = getAnalytics();
        logEvent(analytics, 'screen_view', {
            firebase_screen: 'signin_button_popup'
        });

        const audio = document.getElementById('audio');
        audio?.pause();
        if (!signupOpenLoggedRef.current) {
            signupOpenLoggedRef.current = true;
            trackOnboardingEvent('onboarding_signup_open', { signup_trigger: 'other' });
        }

        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        const unregisterAuthObserver = auth.onAuthStateChanged(user => {
            completeSignin(user);
        });
        return unregisterAuthObserver;
    }, [completeSignin]);

    return (
        <div className={isSheet ? 'previewSigninContent' : 'center'}>
            <div className={`phoneSigninPopup${isSheet ? ' phoneSigninPopupSheet' : ''}`}>
                {isSheet && <div className='previewPreferenceDragIndicator' aria-hidden='true' />}
                <div className='phoneSigninPopupHeader'>
                    <h1 id={headingId} className='phoneSigninPopupTitle'>
                        {signinPending ? 'Signing you in' : signinError ? 'Let’s try again'
                            : authenticated ? 'SIGN IN SUCCESSFUL' : phoneStage === 'code' ? 'Enter your code' : title}
                    </h1>
                    {showPhoneIntroduction &&
                        <div className='phoneSigninPopupSubtitle'>
                            {subtitle}
                        </div>
                    }
                </div>
                {isSheet && showPhoneIntroduction &&
                    <p className='previewSigninPhoneHelp'>We’ll text you a sign-in code.</p>
                }

                {signinPending && <div className='firebaseSigninStatus' role='status'>Finishing sign in…</div>}
                {signinError && <div className='firebaseSigninError'>
                    <p role='alert'>Couldn’t finish signing in. Please try again.</p>
                    <button type='button' className='authSigninPill'
                        onClick={() => completeSignin(auth.currentUser)}>Try again</button>
                </div>}
                <div hidden={!showPhoneForm}>
                    {!authenticated && <PhoneSigninPanel ref={phonePanelRef} onStageChange={setPhoneStage}
                        onBusyChange={setPhoneBusy} autoFocusPhone={autoFocusPhone} hasBackAction />}
                </div>
                {isSheet && <div className='previewSigninSecondaryActions'>
                    {phoneStage === 'code' && showPhoneForm &&
                        <button type='button' className='previewSigninChangeNumber' disabled={busy}
                            onClick={() => phonePanelRef.current?.changeNumber()}>Change phone number</button>
                    }
                    <button type='button' className='previewSigninNotNow' onClick={close} disabled={busy}>Not now</button>
                </div>}
                <button type='button' onClick={close} disabled={busy} className={isSheet ? 'previewSigninClose' : 'popupClose'} aria-label='Close sign in'>
                    <Close aria-hidden='true' />
                </button>
            </div>
        </div>
    );
}

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
})

const mapActionsToProps = {
    signinUser,
}

export default connect(mapStateToProps, mapActionsToProps)(SigninButtonPopup);
